// src/reports/MoneyFlowReport.js
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

/* ---------------- helpers ---------------- */
const dkey = (d) =>
  !d ? "" : typeof d === "string" ? d.slice(0, 10) : format(new Date(d), "yyyy-MM-dd");
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Online card/cash from explicit columns + JSON partners
function getOnlineSumsFromZ(z) {
  let onlineCard = n(z.snappy_card) + n(z.justeat_card) + n(z.deliveroo_card);
  let onlineCash = n(z.snappy_cash) + n(z.justeat_cash) + n(z.deliveroo_cash);
  const partners = z?.online_partners || {};
  if (partners && typeof partners === "object") {
    for (const [k, v] of Object.entries(partners)) {
      const key = k.toLowerCase();
      if (key.endsWith("_card")) onlineCard += n(v);
      if (key.endsWith("_cash")) onlineCash += n(v);
    }
  }
  return { onlineCard, onlineCash };
}

/* --------------- tables ------------------ */
const TBL = {
  ZREPORTS: "zreports",
  CLOCKINS: "clockins",
  EXPENSES: "expenses",
  WASTAGE: "wastage",
  STOCKTAKE: "stocktake",
};

export default function MoneyFlowReport({ fromDate, toDate }) {
  /* what-if knobs + wages input */
  const [oneStopRate, setOneStopRate] = useState(22); // %
  const [onlineRate, setOnlineRate] = useState(11);   // %
  const [hourlyRate, setHourlyRate] = useState(9.5);  // £/hr

  const [viewType, setViewType] = useState("daily"); // daily | weekly | monthly
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fromDate && toDate) fetchData();
  }, [fromDate, toDate, viewType, oneStopRate, onlineRate, hourlyRate]);

  async function fetchData() {
    setLoading(true);
    try {
      const [
        { data: zreports, error: ez },
        { data: clockins, error: ec },
        { data: expenses, error: ee },
        { data: wastage, error: ew },
        { data: stocktakes, error: es },
      ] = await Promise.all([
        supabase.from(TBL.ZREPORTS).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.CLOCKINS).select("*").gte("date", fromDate).lte("date", toDate),
        supabase
          .from(TBL.EXPENSES)
          .select("date,category,staff_name,amount,description")
          .gte("date", fromDate)
          .lte("date", toDate),
        supabase.from(TBL.WASTAGE).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.STOCKTAKE).select("*").gte("date", fromDate).lte("date", toDate),
      ]);
      if (ez || ec || ee || ew || es) {
        const msg = [ez, ec, ee, ew, es].filter(Boolean).map((e) => e.message).join(" · ");
        throw new Error(msg || "Fetch failed");
      }

      // union of dates across all sources
      const dateSet = new Set();
      (zreports || []).forEach((r) => dateSet.add(dkey(r.date)));
      (clockins || []).forEach((r) => dateSet.add(dkey(r.date)));
      (expenses || []).forEach((r) => dateSet.add(dkey(r.date)));
      (wastage || []).forEach((r) => dateSet.add(dkey(r.date)));
      (stocktakes || []).forEach((r) => dateSet.add(dkey(r.date)));
      const allDates = Array.from(dateSet).filter(Boolean).sort();

      // build maps
      const zrMap = {};
      (zreports || []).forEach((z) => {
        const d = dkey(z.date);
        if (!zrMap[d]) zrMap[d] = [];
        zrMap[d].push(z);
      });

      const clkMap = {};
      (clockins || []).forEach((c) => {
        const d = dkey(c.date);
        if (!clkMap[d]) clkMap[d] = { hours: 0, feeOut: 0 };
        clkMap[d].hours += n(c.total_hours_decimal);
        clkMap[d].feeOut += n(c.delivery_fee); // delivery fee paid to drivers
      });

      const paidStaffMap = {};
      const otherExpMap = {};
      (expenses || []).forEach((e) => {
        const d = dkey(e.date);
        const amt = n(e.amount);
        if ((e.category || "").toLowerCase() === "paid to staff") {
          paidStaffMap[d] = (paidStaffMap[d] || 0) + amt;
        } else {
          otherExpMap[d] = (otherExpMap[d] || 0) + amt;
        }
      });

      const wastMap = {};
      (wastage || []).forEach((w) => {
        const d = dkey(w.date);
        wastMap[d] = (wastMap[d] || 0) + n(w.amount);
      });

      const stockVarMap = {};
      (stocktakes || []).forEach((s) => {
        const d = dkey(s.date);
        stockVarMap[d] = (stockVarMap[d] || 0) + n(s.variance);
      });

      // compose rows
      const osRate = oneStopRate / 100;
      const onRate = onlineRate / 100;

      let out = allDates.map((d) => {
        // aggregate zreport fields (if multiple rows same day)
        let oneStopCard = 0,
          oneStopCash = 0,
          onlineCard = 0,
          onlineCash = 0,
          feeInZ = 0,
          varCash = 0,
          supCash = 0,
          supBank = 0;

        for (const z of zrMap[d] || []) {
          oneStopCard += n(z.one_stop_card);
          oneStopCash += n(z.one_stop_cash);
          const o = getOnlineSumsFromZ(z);
          onlineCard += o.onlineCard;
          onlineCash += o.onlineCash;
          feeInZ += n(z.delivery_fee);
          varCash += n(z.variance);
          supCash += n(z.supplier_cash_out);
          supBank += n(z.supplier_bank_out);
        }

        const hrs = clkMap[d]?.hours || 0;
        const feeOutClk = clkMap[d]?.feeOut || 0;
        const paidToStaff = paidStaffMap[d] || 0;
        const otherExp = otherExpMap[d] || 0;
        const wastageAmt = wastMap[d] || 0;
        const stockVar = stockVarMap[d] || 0;

        const oneStopSales = oneStopCard + oneStopCash;
        const onlineSales = onlineCard + onlineCash;

        // GM proxy (what-if): margins on sales + delivery fee in
        const gmProxy =
          osRate * oneStopSales + onRate * onlineSales + feeInZ;

        // Net wages (your definition used everywhere)
        const netWages = hrs * hourlyRate + feeOutClk - paidToStaff;

        // Net Profit reconciliation
        const outflows =
          netWages +
          wastageAmt +
          stockVar +
          supCash +
          supBank +
          varCash + // treat cash variance as an outflow adjustment
          otherExp;

        const netProfit = gmProxy - outflows;

        return {
          date: d,
          oneStopSales,
          onlineSales,
          feeInZ,
          feeOutClk,
          hrs,
          hrRate: hourlyRate,
          paidToStaff,
          netWages,
          wastage: wastageAmt,
          stockVar,
          supCash,
          supBank,
          otherExp,
          varCash,
          gmProxy,
          netProfit,
        };
      });

      // grouping
      if (viewType !== "daily") {
        const g = {};
        out.forEach((r) => {
          const key =
            viewType === "weekly"
              ? format(parseISO(r.date), "RRRR-'W'II")
              : r.date.slice(0, 7);
          if (!g[key]) g[key] = { ...r, date: key };
          else {
            for (const [k, v] of Object.entries(r)) {
              if (k === "date") continue;
              g[key][k] = (g[key][k] || 0) + (typeof v === "number" ? v : 0);
            }
          }
          g[key].hrRate = hourlyRate; // show current rate in grouped view
        });
        out = Object.values(g);
      }

      setRows(out);
    } catch (err) {
      console.error("MoneyFlow fetch error:", err);
      toast.error("MoneyFlow fetch error: " + err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- KPIs & panel ---------------- */
 const totals = useMemo(
  () =>
    rows.reduce((a, r) => {
      for (const [k, v] of Object.entries(r)) {
        if (k === "date" || k === "hrRate") continue; // ← skip hrRate
        if (typeof v === "number") a[k] = (a[k] || 0) + v;
      }
      return a;
    }, {}),
  [rows]
);
  const grossSales = (totals.oneStopSales || 0) + (totals.onlineSales || 0);
  const wagesPct = grossSales ? ((totals.netWages || 0) / grossSales) * 100 : 0;
  const wastagePct = grossSales ? ((totals.wastage || 0) / grossSales) * 100 : 0;

  /* ---------------- CSV ---------------- */
  const exportCSV = () => {
    if (!rows.length) return;
    const header = [
      "Date",
      "OneStop Sales",
      "Online Sales",
      "Delivery Fee In (Z)",
      "Delivery Fee Out (Drivers)",
      "Hours",
      "Hourly Rate",
      "Paid via Expense",
      "Net Wages",
      "Wastage",
      "Stock Variance",
      "Supplier Cash",
      "Supplier Bank",
      "Other Expenses",
      "Cash Variance",
      "GM Proxy (@ rates)",
      "Net Profit",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.date,
          r.oneStopSales.toFixed(2),
          r.onlineSales.toFixed(2),
          r.feeInZ.toFixed(2),
          r.feeOutClk.toFixed(2),
          r.hrs.toFixed(2),
          r.hrRate.toFixed(2),
          r.paidToStaff.toFixed(2),
          r.netWages.toFixed(2),
          r.wastage.toFixed(2),
          r.stockVar.toFixed(2),
          r.supCash.toFixed(2),
          r.supBank.toFixed(2),
          r.otherExp.toFixed(2),
          r.varCash.toFixed(2),
          r.gmProxy.toFixed(2),
          r.netProfit.toFixed(2),
        ].join(",")
      ),
    ];
    saveAs(new Blob([lines.join("\n")], { type: "text/csv" }), `money_flow_${fromDate}_${toDate}.csv`);
  };

  /* ---------------- styles ---------------- */
  const s = {
    card: {
      background: "#F7F9FB",
      border: "1px solid #E2E8F0",
      borderRadius: 8,
      padding: 12,
      minWidth: 180,
    },
    kpiVal: { fontSize: 18, fontWeight: 700 },
    kpiLbl: { fontSize: 12, color: "#607D8B" },
    groupRevenue: { background: "#E8F5E9", color: "#1B5E20" },
    groupAdj: { background: "#FFF8E1", color: "#8D6E63" },
    groupCosts: { background: "#FFEBEE", color: "#B71C1C" },
    groupInfo: { background: "#E3F2FD", color: "#0D47A1" },
    groupSummary: { background: "#ECEFF1", color: "#37474F" },
    tfoot: { background: "#F5F5F5", fontWeight: "bold" },
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => setViewType("daily")} disabled={loading || viewType === "daily"}>Daily</button>
        <button onClick={() => setViewType("weekly")} disabled={loading || viewType === "weekly"}>Weekly</button>
        <button onClick={() => setViewType("monthly")} disabled={loading || viewType === "monthly"}>Monthly</button>
        <button onClick={exportCSV} disabled={!rows.length} style={{ marginLeft: 8 }}>Export CSV</button>

        <div style={{ marginLeft: 12 }}>
          <label>OneStop %:&nbsp;</label>
          <input type="range" min="0" max="40" value={oneStopRate}
                 onChange={(e) => setOneStopRate(Number(e.target.value))} />
          &nbsp; <strong>{oneStopRate}%</strong>
        </div>
        <div>
          <label>Online %:&nbsp;</label>
          <input type="range" min="0" max="30" value={onlineRate}
                 onChange={(e) => setOnlineRate(Number(e.target.value))} />
          &nbsp; <strong>{onlineRate}%</strong>
        </div>
        <div>
          <label>Hourly Rate:&nbsp;</label>
          <input
            type="number"
            step="0.01"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(n(e.target.value))}
            style={{ width: 80 }}
          />
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={s.card}>
          <div style={s.kpiLbl}>Gross Sales</div>
          <div style={s.kpiVal}>£{grossSales.toFixed(2)}</div>
        </div>
        <div style={s.card}>
          <div style={s.kpiLbl}>GM Proxy (@ {oneStopRate}% / {onlineRate}% + Z fee)</div>
          <div style={s.kpiVal}>£{n(totals.gmProxy).toFixed(2)}</div>
        </div>
        <div style={s.card}>
          <div style={s.kpiLbl}>Wages % of Sales</div>
          <div style={s.kpiVal}>{wagesPct.toFixed(1)}%</div>
        </div>
        <div style={s.card}>
          <div style={s.kpiLbl}>Wastage % of Sales</div>
          <div style={s.kpiVal}>{wastagePct.toFixed(1)}%</div>
        </div>
        <div style={s.card}>
          <div style={s.kpiLbl}>Net Profit</div>
          <div style={{ ...s.kpiVal, color: n(totals.netProfit) < 0 ? "crimson" : "green" }}>
            £{n(totals.netProfit).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Reconciliation panel */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "0 0 12px 0" }}>
        <div style={{ ...s.card, minWidth: 260 }}>
          <div style={s.kpiLbl}>Inflows (GM proxy)</div>
          <div style={s.kpiVal}>£{n(totals.gmProxy).toFixed(2)}</div>
        </div>
        <div style={{ ...s.card, minWidth: 420 }}>
          <div style={s.kpiLbl}>Outflows</div>
          <div>
            Wages £{n(totals.netWages).toFixed(2)} • Wastage £{n(totals.wastage).toFixed(2)} • Stock Var £{n(totals.stockVar).toFixed(2)} •
            Suppliers £{(n(totals.supCash) + n(totals.supBank)).toFixed(2)} • Cash Var £{n(totals.varCash).toFixed(2)} • Other £{n(totals.otherExp).toFixed(2)}
          </div>
          <div style={{ ...s.kpiVal, marginTop: 6 }}>£{(
            n(totals.netWages) +
            n(totals.wastage) +
            n(totals.stockVar) +
            n(totals.supCash) +
            n(totals.supBank) +
            n(totals.varCash) +
            n(totals.otherExp)
          ).toFixed(2)}</div>
        </div>
        <div style={{ ...s.card, minWidth: 200 }}>
          <div style={s.kpiLbl}>Inflows − Outflows</div>
          <div style={{ ...s.kpiVal, color: n(totals.netProfit) < 0 ? "crimson" : "green" }}>
            £{n(totals.netProfit).toFixed(2)}
          </div>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : !rows.length ? (
        <p>No data for the selected range.</p>
      ) : (
        <table>
          <thead>
            {/* Group headers */}
            <tr>
              <th rowSpan="2">Date</th>
              <th colSpan="3" style={s.groupRevenue}>Revenue (What-if)</th>
              <th colSpan="1" style={s.groupAdj}>Adjustments</th>
              <th colSpan="6" style={s.groupCosts}>Outflows</th>
              <th colSpan="4" style={s.groupInfo}>Inputs / Context</th>
              <th rowSpan="2" style={s.groupSummary}>Net Profit</th>
            </tr>
            {/* Labels */}
            <tr>
              <th>OneStop Sales</th>
              <th>Online Sales</th>
              <th>GM Proxy</th>

              <th>Cash Variance</th>

              <th>Net Wages</th>
              <th>Wastage</th>
              <th>Stock Var</th>
              <th>Supplier Cash</th>
              <th>Supplier Bank</th>
              <th>Other Expenses</th>

              <th>Delivery Fee In (Z)</th>
              <th>Delivery Fee Out (Drivers)</th>
              <th>Hours</th>
              <th>Hr Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>

                <td style={{ color: "#1B5E20" }}>{r.oneStopSales.toFixed(2)}</td>
                <td style={{ color: "#1B5E20" }}>{r.onlineSales.toFixed(2)}</td>
                <td style={{ color: "#1B5E20", fontWeight: 600 }}>{r.gmProxy.toFixed(2)}</td>

                <td style={{ color: "#8D6E63" }}>{r.varCash.toFixed(2)}</td>

                <td style={{ color: "#B71C1C" }}>{r.netWages.toFixed(2)}</td>
                <td style={{ color: "#B71C1C" }}>{r.wastage.toFixed(2)}</td>
                <td style={{ color: "#B71C1C" }}>{r.stockVar.toFixed(2)}</td>
                <td style={{ color: "#B71C1C" }}>{r.supCash.toFixed(2)}</td>
                <td style={{ color: "#B71C1C" }}>{r.supBank.toFixed(2)}</td>
                <td style={{ color: "#B71C1C" }}>{r.otherExp.toFixed(2)}</td>

                <td style={{ color: "#0D47A1" }}>{r.feeInZ.toFixed(2)}</td>
                <td style={{ color: "#0D47A1" }}>{r.feeOutClk.toFixed(2)}</td>
                <td style={{ color: "#0D47A1" }}>{r.hrs.toFixed(2)}</td>
                <td style={{ color: "#0D47A1" }}>{r.hrRate.toFixed(2)}</td>

                <td style={{ color: r.netProfit < 0 ? "crimson" : "green", fontWeight: 700 }}>
                  {r.netProfit.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={s.tfoot}>
              <th>Totals</th>
              <th>{n(totals.oneStopSales).toFixed(2)}</th>
              <th>{n(totals.onlineSales).toFixed(2)}</th>
              <th>{n(totals.gmProxy).toFixed(2)}</th>

              <th>{n(totals.varCash).toFixed(2)}</th>

              <th>{n(totals.netWages).toFixed(2)}</th>
              <th>{n(totals.wastage).toFixed(2)}</th>
              <th>{n(totals.stockVar).toFixed(2)}</th>
              <th>{n(totals.supCash).toFixed(2)}</th>
              <th>{n(totals.supBank).toFixed(2)}</th>
              <th>{n(totals.otherExp).toFixed(2)}</th>

              <th>{n(totals.feeInZ).toFixed(2)}</th>
              <th>{n(totals.feeOutClk).toFixed(2)}</th>
              <th>{n(totals.hrs).toFixed(2)}</th>
              <th>{hourlyRate.toFixed(2)}</th>

              <th style={{ color: n(totals.netProfit) < 0 ? "crimson" : "green" }}>
                {n(totals.netProfit).toFixed(2)}
              </th>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
